import React, { useState } from "react";
import { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import { PencilIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import Table from "../common/Table";
import CityForm from "../forms/CityForm";
import { useCitiesInfinite } from "../../hooks/useCitiesInfinite";
import {
  City,
  CityFormData,
  CityUpdateFormData,
  CityRequest,
} from "../../schemas/citySchema";

const Cities: React.FC = () => {
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState<City | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [search, setSearch] = useState("");

  const {
    cities,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    createCity,
    updateCity,
    deleteCities,
  } = useCitiesInfinite({ search });

  const handleCreate = () => {
    setSelectedCity(undefined);
    setIsFormModalOpen(true);
  };

  const handleEdit = (city: City) => {
    setSelectedCity(city);
    setIsFormModalOpen(true);
  };

  const handleDelete = async (city: City) => {
    if (window.confirm(`Delete City "${city.name}"?`)) {
      await deleteCities([city.id]);
    }
  };

  const handleBulkDelete = (ids: string[]) => {
    if (window.confirm(`Delete ${ids.length} selected city/cities?`)) {
      deleteCities(ids);
      setRowSelection({});
    }
  };

  const handleSubmit = async (data: CityFormData | CityUpdateFormData) => {
    setIsSubmitting(true);
    try {
      if (selectedCity?.id) {
        await updateCity({
          id: selectedCity.id,
          city: data as CityUpdateFormData,
        });
      } else {
        const payload: CityRequest = {
          name: data.name || "",
        };
        await createCity(payload);
      }
      setIsFormModalOpen(false);
      setSelectedCity(undefined);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setIsFormModalOpen(false);
    setSelectedCity(undefined);
  };

  const columns: ColumnDef<City>[] = [{ accessorKey: "name", header: "Name" }];

  const actions = [
    {
      label: "Edit",
      icon: <PencilIcon className="h-4 w-4" />,
      onClick: handleEdit,
      variant: "success" as const,
    },
    {
      label: "Delete",
      icon: <TrashIcon className="h-4 w-4" />,
      onClick: handleDelete,
      variant: "danger" as const,
    },
  ];

  return (
    <>
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-xl font-bold text-gray-800">Cities</h1>
        <button
          onClick={handleCreate}
          className="p-2 bg-primary-default text-white rounded hover:bg-primary-light"
        >
          <PlusIcon className="h-5 w-5" />
        </button>
      </div>

      <Table
        columns={columns}
        data={cities as City[]}
        actions={actions}
        isLoading={isLoading}
        maxHeight="48vh"
        pagination
        searchable
        infinite
        hasNextPage={hasNextPage}
        isFetchingMore={isFetchingNextPage}
        onLoadMore={() => fetchNextPage()}
        onSearch={setSearch}
        selectable={true}
        getRowId={(city) => city.id}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        bulkActions={[
          {
            label: "Delete Selected",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: handleBulkDelete,
            variant: "danger" as const,
          },
        ]}
      />

      {isFormModalOpen && (
        <CityForm
          isOpen={isFormModalOpen}
          onCancel={handleCancel}
          initialData={selectedCity}
          onSubmit={handleSubmit}
          isLoading={isSubmitting}
          isUpdate={!!selectedCity}
        />
      )}
    </>
  );
};

export default Cities;
